import React, { useMemo } from 'react';
import {
  Card,
  Timeline,
  List,
  Typography,
  Button,
  Row,
  Col,
  Space,
  Flex,
} from 'antd';
import Logo from '../../components/logo/logo';
import { useStore } from '../../helpers/use-store';
import { useTranslation } from 'react-i18next';

const { Title, Paragraph, Text } = Typography;

const AffiliateProgram = () => {
  const { t } = useTranslation();
  const {
    dataStore: { userStore },
  } = useStore();

  const shortLink = userStore.currentUser.affiliateInfo?.shortUrl;

  return (
    <Card
      style={{ maxWidth: '800px', margin: '24px auto', padding: '24px' }}
      bordered={false}
    >
      {/* Header Section */}
      <Flex justify={'center'}>
        <Logo />
      </Flex>
      <Title
        level={2}
        style={{ textAlign: 'center', color: '#1890ff', marginBottom: '16px' }}
      >
        {t('affiliate.program.title')}
      </Title>
      <Paragraph
        style={{ textAlign: 'center', fontSize: '16px', marginBottom: '32px' }}
      >
        {t('affiliate.program.subtitle')}
      </Paragraph>

      {/* Big Button for Registration */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <Button
          type="primary"
          size="large"
          href="http://cc.payproglobal.com/AffiliateSignup/8DD21BDCCF3E213"
          target="_blank"
        >
          {shortLink
            ? t('affiliate.program.dashboard')
            : t('affiliate.program.signUp')}
        </Button>
      </div>

      {/* Key Features with Icons */}
      <Row
        gutter={[24, 24]}
        style={{ marginBottom: '40px', textAlign: 'center' }}
      >
        <Col xs={24} sm={8}>
          <span style={{ fontSize: '32px', marginBottom: '8px' }}>💰</span>
          <Title level={4} style={{ marginBottom: '8px' }}>
            {t('affiliate.program.features.commission.title')}
          </Title>
          <Paragraph style={{ margin: 0 }}>
            {t('affiliate.program.features.commission.desc')}
          </Paragraph>
        </Col>
        <Col xs={24} sm={8}>
          <span style={{ fontSize: '32px', marginBottom: '8px' }}>🔄</span>
          <Title level={4} style={{ marginBottom: '8px' }}>
            {t('affiliate.program.features.lifetime.title')}
          </Title>
          <Paragraph style={{ margin: 0 }}>
            {t('affiliate.program.features.lifetime.desc')}
          </Paragraph>
        </Col>
        <Col xs={24} sm={8}>
          <span style={{ fontSize: '32px', marginBottom: '8px' }}>💳</span>
          <Title level={4} style={{ marginBottom: '8px' }}>
            {t('affiliate.program.features.payouts.title')}
          </Title>
          <Paragraph style={{ margin: 0 }}>
            {t('affiliate.program.features.payouts.desc')}
          </Paragraph>
        </Col>
      </Row>

      {/* Steps to Get Started with Timeline */}
      <Title level={3} style={{ marginBottom: '24px' }}>
        {t('affiliate.program.stepsTitle')}
      </Title>
      <Timeline style={{ marginBottom: '40px' }}>
        <Timeline.Item>
          <strong>{t('affiliate.program.steps.register.label')}:</strong>{' '}
          {t('affiliate.program.steps.register.desc')} 📝
        </Timeline.Item>
        <Timeline.Item>
          <strong>{t('affiliate.program.steps.approve.label')}:</strong>{' '}
          {t('affiliate.program.steps.approve.desc')} ✅
        </Timeline.Item>
        <Timeline.Item>
          <strong>{t('affiliate.program.steps.promote.label')}:</strong>{' '}
          {t('affiliate.program.steps.promote.desc')} 🌟
        </Timeline.Item>
        <Timeline.Item>
          <strong>{t('affiliate.program.steps.earn.label')}:</strong>{' '}
          {t('affiliate.program.steps.earn.desc')} 💰
        </Timeline.Item>
      </Timeline>

      {/* FAQ Section with Emojis */}
      <Title level={3} style={{ marginBottom: '24px' }}>
        {t('affiliate.program.faqTitle')}
      </Title>
      <List
        bordered
        dataSource={[
          {
            emoji: '🤔',
            title: t('affiliate.program.faq.howItWorks.title'),
            content: (
              <>
                {t('affiliate.program.faq.howItWorks.content1')}{' '}
                <Text strong>
                  {t('affiliate.program.faq.howItWorks.commission')}
                </Text>{' '}
                {t('affiliate.program.faq.howItWorks.content2')}
              </>
            ),
          },
          {
            emoji: '💸',
            title: t('affiliate.program.faq.payouts.title'),
            content: (
              <>
                {t('affiliate.program.faq.payouts.content1')}{' '}
                <Text strong>{t('affiliate.program.faq.payouts.monthly')}</Text>
                {t('affiliate.program.faq.payouts.content2')}
              </>
            ),
          },
          {
            emoji: '📊',
            title: t('affiliate.program.faq.tracking.title'),
            content: t('affiliate.program.faq.tracking.content'),
          },
          {
            emoji: '📅',
            title: t('affiliate.program.faq.attribution.title'),
            content: (
              <>
                {t('affiliate.program.faq.attribution.content1')}{' '}
                <Text strong>
                  {t('affiliate.program.faq.attribution.period')}
                </Text>
                {t('affiliate.program.faq.attribution.content2')}
              </>
            ),
          },
          {
            emoji: '🌌',
            title: t('affiliate.program.faq.earnings.title'),
            content: (
              <>
                {t('affiliate.program.faq.earnings.content1')}{' '}
                <Text strong>
                  {t('affiliate.program.faq.earnings.commission')}
                </Text>
                {t('affiliate.program.faq.earnings.content2')}
              </>
            ),
          },
          {
            emoji: '🚫',
            title: t('affiliate.program.faq.ads.title'),
            content: <>{t('affiliate.program.faq.ads.content')}</>,
          },
          {
            emoji: '📧',
            title: t('affiliate.program.faq.email.title'),
            content: (
              <>
                {t('affiliate.program.faq.email.content1')}
                <br />
                <br />
                <Text strong>
                  ⚠️ {t('affiliate.program.faq.email.important')}:
                </Text>{' '}
                {t('affiliate.program.faq.email.content2')}
              </>
            ),
          },
          {
            emoji: '😊',
            title: t('affiliate.program.faq.cost.title'),
            content: t('affiliate.program.faq.cost.content'),
          },
        ]}
        renderItem={(item) => (
          <List.Item>
            <Space align="start">
              <span style={{ fontSize: '18px' }}>{item.emoji}</span>
              <div>
                <strong>{item.title}</strong>
                <Paragraph style={{ marginTop: '8px', marginBottom: 0 }}>
                  {item.content}
                </Paragraph>
              </div>
            </Space>
          </List.Item>
        )}
      />
    </Card>
  );
};

export default AffiliateProgram;
